import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Check, Loader2, ChevronDown, ChevronUp, Play, RotateCcw, GraduationCap, ChevronRight } from "lucide-react";
import AppShell from "@/components/AppShell";
import CefrBadge from "@/components/CefrBadge";
import client from "@/lib/api";
import { useLang } from "@/context/LangContext";

export default function Learn() {
  const nav = useNavigate();
  const { t } = useLang();
  const [state, setState] = useState(null);
  const [course, setCourse] = useState(null);
  const [activeLevel, setActiveLevel] = useState("A1");
  const [openModule, setOpenModule] = useState(null);

  useEffect(() => {
    client.get("/learn/state").then(({ data }) => {
      if (!data.active_language) { nav("/choose-language"); return; }
      setState(data);
      client.get(`/learn/course/${data.active_language}`).then((r) => {
        setCourse(r.data);
        const cur = data.languages.find((l) => l.code === data.active_language)?.level || "A1";
        setActiveLevel(cur);
        const lv = r.data.levels.find((x) => x.code === cur);
        const firstOpen = lv?.modules.find((m) => m.status === "unlocked" && !m.coming_soon);
        setOpenModule(firstOpen?.id || null);
      });
    });
  }, [nav]);

  if (!state || !course) return <AppShell><div className="pt-24 flex justify-center"><Loader2 className="animate-spin text-[#2563EB]" /></div></AppShell>;

  const langPill = state.languages.find((l) => l.code === state.active_language);
  const level = course.levels.find((l) => l.code === activeLevel);

  return (
    <AppShell>
      <div className="pt-10 pb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-2xl">{langPill?.flag}</span>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight text-[#0F172A]">{langPill?.name}</h1>
        </div>
        <CefrBadge level={activeLevel} size="lg" />
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid="cefr-selector">
        {course.levels.map((l) => (
          <button key={l.code} onClick={() => { setActiveLevel(l.code); setOpenModule(null); }}
            data-testid={`level-pill-${l.code}`}
            className={`shrink-0 px-4 py-2 rounded-xl font-bold text-sm transition-all ${activeLevel === l.code ? "bg-[#2563EB] text-white" : l.unlocked ? "bg-white border border-slate-200 text-[#0F172A]" : "bg-slate-100 text-slate-400"}`}>
            {l.code}{!l.unlocked && <Lock size={12} className="inline ml-1 -mt-0.5" />}
          </button>
        ))}
      </div>

      {level && (
        <div className="mt-3 rounded-2xl bg-gradient-to-br from-[#2563EB] to-[#3B82F6] p-5 text-white shadow-[0_16px_40px_-20px_rgba(30,86,160,0.9)]">
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-bold uppercase tracking-widest opacity-90">{level.code} · {level.name}</p>
            {level.content_status && level.content_status !== "COMPLETE" && (
              <span data-testid="level-status" className="text-[10px] font-extrabold uppercase tracking-wide bg-white/20 rounded-full px-2 py-0.5">
                {{ COMING_SOON: "Coming soon", IN_PROGRESS: "In progress", PARTIALLY_PUBLISHED: "Partial" }[level.content_status]}
              </span>
            )}
          </div>
          <div className="flex items-end justify-between mt-1">
            <p className="font-heading text-lg font-extrabold">{t("learn_percent_complete", { p: level.percent })}</p>
            <p className="text-xs opacity-90">{t("learn_lessons_frac", { a: level.lessons_done, b: level.lessons_total })}</p>
          </div>
          <div className="mt-2 h-2 rounded-full bg-white/25 overflow-hidden">
            <div className="h-full bg-white rounded-full" style={{ width: `${level.percent}%` }} />
          </div>
        </div>
      )}

      {["de", "en"].includes(course.language) && (
        <button onClick={() => nav(`/placement/${course.language}`)} data-testid="placement-cta"
          className="mt-3 w-full flex items-center justify-between rounded-2xl bg-white border border-slate-200/70 px-4 py-3">
          <span className="flex items-center gap-2.5">
            <span className="w-9 h-9 rounded-xl bg-[#8B5CF6]/10 text-[#8B5CF6] flex items-center justify-center"><GraduationCap size={18} /></span>
            <span className="text-left"><span className="block text-sm font-bold text-[#0F172A]">{t("learn_not_beginner")}</span><span className="block text-xs text-[#475569]">{t("learn_placement_cta")}</span></span>
          </span>
          <ChevronRight size={18} className="text-slate-400" />
        </button>
      )}

      {level?.coming_soon && (
        <div className="mt-6 text-center text-slate-400 text-sm" data-testid="level-coming-soon">Content for this level is coming soon.</div>
      )}

      {(() => {
        const next = course.levels.find((l) => !l.unlocked && l.gate);
        if (!next) return null;
        const hPct = Math.min(100, Math.round((next.gate.hours_have / next.gate.hours_needed) * 100));
        const dPct = Math.min(100, Math.round((next.gate.active_days_have / next.gate.active_days_needed) * 100));
        return (
          <div className="mt-4 rounded-2xl bg-white border border-slate-200/70 p-4" data-testid="next-level-gate">
            <div className="flex items-center gap-2">
              <Lock size={14} className="text-slate-400" />
              <p className="text-sm font-bold text-[#0F172A]">{t("learn_unlock")} <CefrBadge level={next.code} size="sm" className="mx-1" /></p>
            </div>
            <p className="text-xs text-[#475569] mt-1">{t("learn_earned")}</p>
            <div className="mt-3 space-y-2.5">
              <div>
                <div className="flex justify-between text-[11px] font-semibold text-[#475569]"><span>{t("learn_hours")}</span><span>{next.gate.hours_have} / {next.gate.hours_needed}h</span></div>
                <div className="mt-1 h-1.5 rounded-full bg-[#EDF2F7] overflow-hidden"><div className="h-full rounded-full bg-[#2563EB]" style={{ width: `${hPct}%` }} /></div>
              </div>
              <div>
                <div className="flex justify-between text-[11px] font-semibold text-[#475569]"><span>{t("learn_active_days")}</span><span>{next.gate.active_days_have} / {next.gate.active_days_needed}</span></div>
                <div className="mt-1 h-1.5 rounded-full bg-[#EDF2F7] overflow-hidden"><div className="h-full rounded-full bg-[#8B5CF6]" style={{ width: `${dPct}%` }} /></div>
              </div>
            </div>
          </div>
        );
      })()}

      <div className="mt-4 space-y-3">
        {level?.modules.map((m) => {
          const locked = m.status === "locked";
          const open = openModule === m.id;
          return (
            <div key={m.id} className={`rounded-2xl border overflow-hidden ${locked ? "border-slate-200 bg-slate-50" : "border-slate-200 bg-white"}`} data-testid={`module-${m.n}`}>
              <button onClick={() => !locked && setOpenModule(open ? null : m.id)} disabled={locked}
                className="w-full flex items-center gap-3 p-3 text-left">
                <div className="relative w-14 h-14 rounded-xl overflow-hidden shrink-0 bg-slate-200">
                  {m.cover && <img src={m.cover} alt="" className={`w-full h-full object-cover ${locked ? "grayscale opacity-60" : ""}`} />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">{t("learn_module")} {m.n}</p>
                  <p className="font-bold text-[#0F172A] truncate">{m.title}</p>
                  <p className="text-xs text-slate-400">{m.coming_soon ? t("learn_coming_soon") : t("learn_lessons_count", { a: m.lessons_done, b: m.lesson_count })}</p>
                </div>
                {locked ? <Lock size={18} className="text-slate-400" /> : (open ? <ChevronUp className="text-slate-400" /> : <ChevronDown className="text-slate-400" />)}
              </button>
              {open && !locked && (
                <div className="px-3 pb-3 space-y-2">
                  {m.coming_soon && <p className="text-sm text-slate-400 py-2">Lessons coming soon.</p>}
                  {m.lessons.map((ls) => {
                    const llocked = ls.status === "locked";
                    const done = ls.status === "completed";
                    return (
                      <button key={ls.id} onClick={() => !llocked && nav(`/lesson/${ls.id}`)} disabled={llocked}
                        data-testid={`lesson-item-${ls.id}`}
                        className={`w-full flex items-center gap-3 rounded-xl p-3 border ${llocked ? "border-slate-100 bg-slate-50" : "border-slate-100 bg-white hover:border-[#2563EB]/40"}`}>
                        <span className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${done ? "bg-emerald-500 text-white" : llocked ? "bg-slate-200 text-slate-400" : "bg-[#2563EB]/10 text-[#2563EB]"}`}>
                          {done ? <Check size={16} /> : llocked ? <Lock size={13} /> : ls.n}
                        </span>
                        <span className="flex-1 text-left min-w-0">
                          <span className="block font-semibold text-[#0F172A] text-sm truncate">{ls.title}</span>
                          <span className="block text-xs text-slate-400">{ls.minutes} {t("common_min")} · {t("lesson_words_n", { n: ls.word_count })}</span>
                        </span>
                        {!llocked && !done && <Play size={16} className="text-[#2563EB]" />}
                      </button>
                    );
                  })}
                  {m.lessons_done >= 1 && !m.coming_soon && (
                    <button onClick={() => nav(`/module-review/${m.id}`)} data-testid={`module-review-btn-${m.n}`}
                      className="w-full flex items-center gap-3 rounded-xl p-3 border border-[#2563EB]/30 bg-[#2563EB]/5">
                      <span className="w-8 h-8 rounded-full bg-[#2563EB]/15 text-[#2563EB] flex items-center justify-center"><RotateCcw size={15} /></span>
                      <span className="flex-1 text-left font-semibold text-[#2563EB] text-sm">Module Review</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </AppShell>
  );
}
